import Button from '@/components/Button';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import Spinner from '@/components/Spinner';
import { FiCheck, FiStar, LiaCubesSolid, MdOutlineDashboardCustomize } from '@/components/react-icons/main';
import DividerV2 from '@/components/v2/DividerV2';
import IconV2 from '@/components/v2/IconV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import { useChronometer } from '@/hooks/useChronometer';
import setQuery from '@/lib/setQuery';
import { closeLiveWorkflowLogsDrawer } from '@/redux/actions/uiActions/drawerActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import statusIconMap from '@/schema/statusIconMap';
import openWorkflowLogsModal from '@/screens/workflows/lib/openWorkflowLogsModal';
import submitTutorialEvent from '@/screens/workflows/redux/actions/submitTutorialEvent';
import actionStepsSelector from '@/screens/workflows/redux/selectors/actionStepsSelector';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import LogWorkDoneRow from '@/screens/workflows/workflowLogs/LogWorkDoneRow';
import { Fragment, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import SliderHeader from './arguments/argumentOptionsSlider/SliderHeader';

const SELECTOR_SLUGS = ['appSelector', 'triggerSelector', 'appTriggerSelector'];

const LiveWorkflowLogsDrawer = () => {
  const { open } = useSelector((state) => state.ui.liveWorkflowLogsDrawer);
  const isRunning = useSelector(workflowScreenSelectors.workflowIsRunning);
  const steps = useSelector(actionStepsSelector);
  const workflowRunUpdateStepId = useSelector((state) => workflowScreenSelectors.workflowRunUpdate(state)?.stepId);
  const workflowLogOperationLogs = useSelector(memoizeSelector('liveWorkflowLogsDrawer:workflowLogOperationLogs', workflowScreenSelectors.workflowLiveOperationLogs));
  const workflowRunUpdateLogId = useSelector((state) => workflowScreenSelectors.workflowRunUpdate(state)?.workflowLogId);
  const stepRunCount = useSelector(workflowScreenSelectors.workflowStepRunCount);
  const { formattedTime } = useChronometer(open && isRunning);
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const hasWorkDone = Array.isArray(workflowLogOperationLogs) && workflowLogOperationLogs.length > 0;

  const getStepRunCount = (stepId) => stepRunCount?.[stepId] || 0;

  const stepsData = useSelector(
    memoizeSelector('liveWorkflowLogsDrawer:stepsData', (state) => {
      const currentSteps = actionStepsSelector(state);
      if (!currentSteps) {
        return {};
      }
      return currentSteps.reduce((acc, step) => {
        const stepFull = workflowScreenSelectors.activeWorkflowStepById(state, step?.id);
        const app = workflowScreenSelectors.activeWorkflowStepAppByStepId(state, step?.id);
        const stepFunctionId = stepFull?.functionId;
        const stepFunctionName = workflowScreenSelectors.stepFunctionById(state, stepFunctionId)?.name;
        const isFunction = stepFunctionId && !!stepFunctionName;
        const isSelector = SELECTOR_SLUGS.includes(app?.slug);
        const isTrigger = app?.type === 'trigger';
        const category = isSelector ? 'selector' : isTrigger ? 'trigger' : app?.category;

        const stepIcon = isFunction ? LiaCubesSolid : app?.icon || utilityAppIcons[app?.slug] || FiStar;

        acc[step?.id] = { app, stepIcon, isSelector, category, isFunction };
        return acc;
      }, {});
    })
  );

  const getStepData = (stepId) => {
    return stepsData?.[stepId] || { app: null, stepIcon: FiStar, isSelector: false, category: null, isFunction: false };
  };

  const stepsWithData = useMemo(() => {
    return steps?.map((step) => {
      const stepData = getStepData(step?.id);
      return { ...step, stepData };
    });
  }, [steps, stepsData]);

  const onClickViewDetailedLogs = () => {
    openWorkflowLogsModal({ workflowId, workflowLogId: workflowRunUpdateLogId });
    closeLiveWorkflowLogsDrawer();
  };

  const handleClose = () => {
    submitTutorialEvent('info');
    closeLiveWorkflowLogsDrawer();
  };

  useEffect(() => {
    if (open) {
      setQuery({ runSliderOpen: true });
    } else {
      setQuery({ runSliderOpen: undefined });
    }
  }, [open]);

  return (
    <div className={`comp-argument-option-slider ${open ? 'open' : ''}`}>
      <div className={'backing'} onClick={handleClose} />
      <div className={'content live-workflow-logs-slider'}>
        <SliderHeader title="Live Workflow" onClose={handleClose} />
        <div className="logs-container">
          <div className="running-status">
            <div className="running-status-text">
              <div>Workflow {isRunning ? 'is running' : 'has finished running'}</div>
              {isRunning ? <Spinner /> : <IconV2 icon={statusIconMap('success')} className="valid" />}
            </div>
            <div className="ellapsed-time">{formattedTime}</div>
          </div>
          <ScrollContainerV2 className="logs-content">
            {stepsWithData?.map((step, index) => (
              <Fragment key={step?.id}>
                <div className="step-entry">
                  <div className={`info-and-icon ${getStepRunCount(step?.id) === 0 ? 'disabled' : ''} ${workflowRunUpdateStepId === step?.id ? 'running' : ''}`} data-category={step?.stepData?.category} data-is-selector={step?.stepData?.isSelector}>
                    <IconV2 icon={step?.stepData?.stepIcon} className="app-icon" title={step?.stepData?.app?.name} />
                    <span className="step-number">{step?.stepNumber}</span>
                  </div>
                  <span className={`step-name ${getStepRunCount(step?.id) === 0 ? 'disabled' : ''}`}>{step?.message}</span>
                  {getStepRunCount(step?.id) > 0 && <span className="amount-runs">{step?.id === workflowRunUpdateStepId ? 'Running' : `Ran ${getStepRunCount(step?.id)} ${getStepRunCount(step?.id) === 1 ? 'time' : 'times'}`}</span>}
                  {workflowRunUpdateStepId === step?.id ? <Spinner size={14} className={getStepRunCount(step?.id) === 0 ? 'disabled' : ''} /> : getStepRunCount(step?.id) > 0 ? <IconV2 icon={FiCheck} className="valid" /> : null}
                </div>
                {index !== stepsWithData.length - 1 && <DividerV2 />}
              </Fragment>
            ))}
            {!isRunning && (!stepsWithData || stepsWithData.length === 0) && (
              <EmptyStateNotice //
                icon={MdOutlineDashboardCustomize}
                size="sm"
                className="workflow"
                title={'No steps to run yet'}
                description={'This workflow is empty. Add a step, then run it again.'}
              />
            )}
          </ScrollContainerV2>
        </div>

        <div className={`live-work-done ${hasWorkDone ? 'has-content' : ''}`}>
          {hasWorkDone && (
            <>
              <div className="running-status">
                <div className="running-status-text">
                  <div>Work Done</div>
                </div>
              </div>
              <ScrollContainerV2 className="work-done-items">
                {workflowLogOperationLogs?.map((item, index) => (
                  <LogWorkDoneRow key={index} update={item} hideStepNumber hideId noOnClick />
                ))}
              </ScrollContainerV2>
            </>
          )}
        </div>

        {!isRunning && (
          <div className="footer">
            <Button className="primary" size="xl" onClick={onClickViewDetailedLogs}>
              View Detailed Logs
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveWorkflowLogsDrawer;
